import React, { FC, useEffect, useState } from "react";
import {
  SplitDropDown,
  NavPills,
  DashboardTopBar,
  ItemContentArea,
  UpsertPaymentPopUp,
  RelatedSubscriptionGrid,
  RelatedInvoicesGrid,
  RelatedOrdersGrid,
} from "../../../shared/components";
import { observer } from "mobx-react";
import { NavigateFunction, Params } from "react-router-dom";
import DetailSection from "./DetailSection";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import CancelPaymentPopUp from "./CancelPaymentPopUp";
import CancelIcon from "../../../../../../images/products/cross-blue.svg";
import { firstValueFrom } from "rxjs";
import UpsertRefundPopUp from "./UpsertRefundPopUp";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  NavigationButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  ItemsStore,
  PaymentStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { PaymentService } from "../../../shared/services";
import {
  PaymentDetailPageModel,
  SelectedItemModel,
  UpsertPaymentModel,
} from "../../../shared/models";
import {
  ItemDetailMainWrapper,
  MainOuterCard,
  SubscriptionStylesWrapper,
} from "../../../shared/styles";
import { NewPaymentRelatedSource } from "../../../shared/enums";
import VoidPaymentPopUp from "./VoidPaymentPopUp";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";

type IProps = {
  paymentStore: PaymentStore;
  params: Readonly<Params<string>>;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

const PaymentDetailMain: FC<IProps> = ({
  params,
  paymentStore,
  itemStore,
  navigate,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showEditPopUp, setShowEditPopUp] = React.useState(false);
  const [showCancelPopUp, setShowCancelPopUp] = React.useState(false);
  const [showRefundPopUp, setShowRefundPopUp] = React.useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showVoidPopUp, setShowVoidPopUp] = useState(false);
  const [activeNavPill, setActiveNavPill] = useState("details");

  useEffect(() => {
    setShowLoader(true);
    fetchPaymentDetails();
    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, [paymentId()]);

  const fetchPaymentDetails = async () => {
    const response = await firstValueFrom(
      PaymentService.getPayment(
        AuthService.authData.accountId,
        paymentId(),
        paymentStore.searchValue
      )
    );
    const details = PaymentDetailPageModel.deserializeAPIModel(response.result);
    setShowLoader(false);
    paymentStore.updateDetailPage(details);
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/payments",
        title: details.detail?.description || "",
        icon: "",
      })
    );
  };

  function paymentId() {
    const id = params.id || "";
    return UtilityMethods.isValidObjectId(id) ? id : "";
  }

  if (paymentStore.detailPage === null) return null;

  const navPills = () => {
    return (
      <FlexContainer alignItems="center" padding="0px 0px 50px">
        <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
          Payment
        </CustomLabel>
        <div className="flex-auto-grow">
          {false && (
            <NavPills
              defaultActiveKey="details"
              pills={[
                {
                  key: "details",
                  title: "DETAILS",
                },
                {
                  key: "activity",
                  title: "ACTIVITY",
                },
              ]}
              onSelect={(key) => setActiveNavPill(key)}
            />
          )}
        </div>
      </FlexContainer>
    );
  };

  function actionDropdown() {
    const options: any = [];
    const detailPage = paymentStore.detailPage?.detail;

    if (detailPage?.status === "Paid") {
      if (detailPage.refundAmount === 0 && detailPage.showVoidButton) {
        options.push({
          icon: CancelIcon,
          label: "Void",
          value: "void",
          onClick: () => {
            if (!paymentId()) return;
            if (!rolesAndPermissionStore.hasManagePermission("Void payment"))
              return;
            setShowVoidPopUp(true);
          },
        });
      }

      //ONLY SHOW REFUND IF VOID IS NOT AVAILABLE
      if (
        !detailPage.showVoidButton &&
        detailPage.grossAmount > detailPage.refundAmount
      ) {
        options.push({
          icon: EditIcon,
          label: "Refund",
          value: "Refund",
          onClick: () => {
            if (!paymentId()) return;
            if (!rolesAndPermissionStore.hasManagePermission("Refund payment"))
              return;
            setShowRefundPopUp(true);
          },
        });
      }
    } else {
      if (paymentStore.detailPage?.detail?.status === "Scheduled") {
        options.push({
          icon: EditIcon,
          label: "Edit",
          value: "edit",

          onClick: () => {
            if (!paymentId()) return;
            if (!rolesAndPermissionStore.hasManagePermission("Edit payment"))
              return;
            setShowEditPopUp(true);
          },
        });
      }
    }
    // options.push({
    //     icon: DeleteIcon,
    //     label: 'Delete',
    //     value: 'delete',

    //     onClick: () => {
    //         if (!paymentId()) return;

    //         setShowDeletePopUp(true);
    //     },
    // });

    if (paymentStore.detailPage?.detail.status === "Scheduled") {
      options.push({
        icon: CancelIcon,
        label: "Cancel",
        value: "cancel",
        onClick: () => {
          if (!paymentId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Cancel")) return;
          setShowCancelPopUp(true);
        },
      });
    }

    if (options.length === 0) return null;

    function getDropDownHeader() {
      if (paymentStore.detailPage?.detail?.status === "Paid") {
        if (paymentStore.detailPage?.detail?.showVoidButton) {
          return "Void";
        }
        return "Refund";
      }
      return "Edit";
    }

    function onDropDownHeaderClick() {
      if (paymentStore.detailPage?.detail?.status === "Paid") {
        if (paymentStore.detailPage?.detail?.showVoidButton) {
          setShowVoidPopUp(true);
        } else {
          setShowRefundPopUp(true);
        }
      } else {
        setShowEditPopUp(true);
      }
    }

    return (
      <SplitDropDown
        label={options[0].label}
        onClick={() => {
          if (!paymentId()) return;
          if (!rolesAndPermissionStore.hasManagePermission(getDropDownHeader()))
            return;
          onDropDownHeaderClick();
        }}
        className="edit-dropdown-item"
        options={options}
      />
    );
  }

  function renderDetails(node: React.ReactNode) {
    if (!paymentStore.detailPage) return null;
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
          rightContent={
            <>
              <NavigationButtons
                currentIndex={paymentStore.detailPage.index}
                totalItems={paymentStore.detailPage.totalPayments}
                previousRecordId={paymentStore.detailPage.previousRecordId}
                nextRecordId={paymentStore.detailPage.nextRecordId}
                onNavigate={(id) => navigate(`/app/payments/detail/${id}`)}
              />
              {actionDropdown()}
            </>
          }
          merchantAccountStore={merchantAccountStore}
        />
        <ItemContentArea
          node={node}
          itemStore={itemStore}
          showLoader={showLoader}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function voidPaymentPopUp() {
    if (!paymentStore.detailPage) return null;
    // if (!paymentStore.detailPage) return null;
    // const body = (
    //     <>
    //         Voiding a payment will cancel settlement.
    //         <br /> Are you sure you want to void this{' '}
    //         <b>${UtilityMethods.numberWithCommas(paymentStore.detailPage?.detail.netAmount)}</b> payment? This
    //         action cannot be undone.
    //         {voidPaymentError && (
    //             <CustomLabel color="#FF0000" fontSize="16px" margin="20px 0px 0px 0px">
    //                 {voidPaymentError}
    //             </CustomLabel>
    //         )}
    //     </>
    // );
    return (
      <>
        {showVoidPopUp && (
          <VoidPaymentPopUp
            paymentData={paymentStore.detailPage.detail}
            onClose={(refresh: boolean, issueRefund) => {
              setShowVoidPopUp(false);
              (refresh || issueRefund) && fetchPaymentDetails();
            }}
          />
        )}
      </>
    );
  }
  // function voidPaymentTransaction() {
  //     if (!paymentStore.detailPage?.detail) return;
  //     const amount = paymentStore.detailPage.detail.netAmount;
  //     PaymentService.voidPayment(paymentStore.detailPage?.detail?.id || '', AuthService.authData.accountId).subscribe(
  //         {
  //             next: (response) => {
  //                 if (response.result.success) {
  //                     setShowVoidPopUp(false);
  //                     ToastService.showSuccess('Payment voided successfully');
  //                     fetchPaymentDetails();
  //                 } else {
  //                     switch (response.result.failureReason) {
  //                         case 'AmountRefunded':
  //                         case 'GatewayError':
  //                             setVoidPaymentError(
  //                                 `Your payment for $${UtilityMethods.numberWithCommas(amount)} could not be voided. You can issue a refund instead.`,
  //                             );
  //                             break;
  //                     }
  //                     ToastService.showError('Error in voiding payment');
  //                 }
  //             },
  //             error: () => {
  //                 ToastService.showError('Error in voiding payment');
  //             },
  //         },
  //     );
  // }

  return renderDetails(
    <ItemDetailMainWrapper>
      {voidPaymentPopUp()}
      {showCancelPopUp && (
        <CancelPaymentPopUp
          paymentData={paymentStore.detailPage.detail}
          onClose={(refresh: boolean) => {
            setShowCancelPopUp(false);
            refresh && fetchPaymentDetails();
          }}
        />
      )}
      {showEditPopUp && (
        <UpsertPaymentPopUp
          paymentData={UpsertPaymentModel.createModelFromPaymentDetail(
            paymentStore.detailPage.detail
          )}
          onClose={(refresh: boolean) => {
            setShowEditPopUp(false);
            refresh && fetchPaymentDetails();
          }}
          countriesWithState={itemStore.countryStore.countriesWithState}
        />
      )}
      {showRefundPopUp && (
        <UpsertRefundPopUp
          refunds={paymentStore.detailPage.refunds}
          paymentData={UpsertPaymentModel.createModelFromPaymentDetail(
            paymentStore.detailPage.detail
          )}
          onClose={(refresh: boolean) => {
            setShowRefundPopUp(false);
            refresh && fetchPaymentDetails();
          }}
        />
      )}
      <SubscriptionStylesWrapper>
        {" "}
        <MainOuterCard>
          {navPills()}
          {/* <ItemDetailLeftPanel width="100%"> */}
          {activeNavPill === "details" && (
            <DetailSection
              data={paymentStore.detailPage.detail}
              subscription={paymentStore.detailPage.subscriptions?.[0]}
            />
          )}
          {/* </ItemDetailLeftPanel> */}
        </MainOuterCard>
        {activeNavPill === "details" && (
          <>
            <RelatedSubscriptionGrid
              isHideHeaderButton={true}
              countriesWithState={itemStore.countryStore.countriesWithState}
              subscriptions={paymentStore.detailPage.subscriptions}
              onUpdate={() => fetchPaymentDetails()}
              selectedContact={paymentStore.detailPage.detail.contact}
              rolesAndPermissionStore={rolesAndPermissionStore}
            />
            <RelatedOrdersGrid
              orders={paymentStore.detailPage.orders}
              rolesAndPermissionStore={rolesAndPermissionStore}
              countriesWithState={itemStore.countryStore.countriesWithState}
            />
            <RelatedInvoicesGrid
              invoices={paymentStore.detailPage.invoices}
              contact={paymentStore.detailPage.detail.contact}
              countriesWithState={itemStore.countryStore.countriesWithState}
              onUpdate={() => fetchPaymentDetails()}
              source={NewPaymentRelatedSource.Manual}
              rolesAndPermissionStore={rolesAndPermissionStore}
              sourceId={null}
            />
          </>
        )}
      </SubscriptionStylesWrapper>
    </ItemDetailMainWrapper>
  );
};
export default withRouter(observer(PaymentDetailMain));
